import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../design/icons';

export default function DatePicker({ value, onChange, style }) {
  const [open, setOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0, width: 0 });
  const [viewing, setViewing] = useState(() => value ? new Date(value + 'T00:00:00') : new Date());
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        popoverRef.current && !popoverRef.current.contains(e.target)
      ) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPopoverPos({ top: rect.bottom + 8, left: rect.left, width: rect.width });
    }
    setOpen(o => !o);
  };

  const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const firstDayOfMonth = (y, m) => new Date(y, m, 1).getDay();

  const year = viewing.getFullYear();
  const month = viewing.getMonth();
  const selected = value ? new Date(value + 'T00:00:00') : null;
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const prevMonth = () => setViewing(new Date(year, month - 1, 1));
  const nextMonth = () => setViewing(new Date(year, month + 1, 1));

  const selectDay = (day) => {
    const m = String(month + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    onChange(`${year}-${m}-${d}`);
    setOpen(false);
  };

  const displayValue = value
    ? new Date(value + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Select date';

  const totalCells = 42;
  const startPad = firstDayOfMonth(year, month);
  const days = daysInMonth(year, month);

  const popover = open && createPortal(
    <div ref={popoverRef} style={{
      position: 'fixed',
      top: window.innerWidth > 640 ? popoverPos.top : 'auto',
      bottom: window.innerWidth <= 640 ? '20px' : 'auto',
      left: window.innerWidth > 640 ? popoverPos.left : '20px',
      right: window.innerWidth <= 640 ? '20px' : 'auto',
      width: window.innerWidth > 640 ? Math.max(popoverPos.width, 280) : 'auto',
      zIndex: 9999,
      padding: 16,
      borderRadius: 16,
      background: 'var(--cal-bg)',
      border: '1px solid var(--line)',
      boxShadow: 'var(--shadow-lg)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <button type="button" onClick={prevMonth} className="pressable iconwrap" style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid var(--line)', background: 'var(--surface-2)', color: 'var(--ink)' }}>
          <Icon name="chevL" size={14} />
        </button>
        <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}>
          {viewing.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </span>
        <button type="button" onClick={nextMonth} className="pressable iconwrap" style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid var(--line)', background: 'var(--surface-2)', color: 'var(--ink)' }}>
          <Icon name="chevR" size={14} />
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', marginBottom: 6 }}>
        {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 600, color: 'var(--ink-3)', letterSpacing: '0.5px', padding: '4px 0' }}>{d}</div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
        {Array.from({ length: totalCells }, (_, i) => {
          const day = i - startPad + 1;
          const valid = day >= 1 && day <= days;
          if (!valid) return <div key={i} />;

          const thisDate = new Date(year, month, day);
          thisDate.setHours(0, 0, 0, 0);
          const isSelected = selected && thisDate.getTime() === selected.getTime();
          const isToday = thisDate.getTime() === today.getTime();

          return (
            <button
              key={i}
              type="button"
              onClick={() => selectDay(day)}
              style={{
                width: '100%', aspectRatio: '1', borderRadius: 8, border: 'none',
                fontSize: 13, fontWeight: isSelected ? 600 : 400, cursor: 'pointer',
                background: isSelected ? 'var(--accent)' : isToday ? 'var(--accent-soft)' : 'transparent',
                color: isSelected ? 'var(--bg)' : isToday ? 'var(--accent)' : 'var(--ink-2)',
                outline: isToday && !isSelected ? '1px solid color-mix(in srgb, var(--accent) 40%, transparent)' : 'none',
                transition: 'background 0.15s',
              }}
            >
              {day}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => {
          const t = new Date();
          onChange(`${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}-${String(t.getDate()).padStart(2,'0')}`);
          setOpen(false);
        }}
        className="chip pressable"
        style={{ marginTop: 12, width: '100%', justifyContent: 'center' }}
      >
        Today
      </button>
    </div>,
    document.body
  );

  return (
    <div className="gl-input" ref={triggerRef} style={{ position: 'relative', width: '100%' }}>
      <button
        type="button"
        onClick={handleOpen}
        style={{
          ...style,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer', textAlign: 'left', width: '100%',
          color: value ? 'var(--ink)' : 'var(--ink-3)',
        }}
      >
        <span>{displayValue}</span>
        <Icon name="calendar" size={15} style={{ color: 'var(--ink-3)' }} />
      </button>
      {popover}
    </div>
  );
}
